import { useEffect, useRef } from 'react'

import { HotkeyValue, KbdShortcuts } from '@/components/ui/KbdShortcuts'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip'

type CellToolbarTooltipPayload = {
  label: string
  shortcut?: HotkeyValue
}

const cellToolbarTooltipHandle =
  TooltipPrimitive.createHandle<CellToolbarTooltipPayload>()

/**
 * Root component for {@link CellToolbarTooltip} triggers.
 */
function CellToolbarTooltipRoot() {
  return (
    <Tooltip handle={cellToolbarTooltipHandle}>
      {({ payload }) => (
        <TooltipContent
          side="top"
          sideOffset={12}
        >
          <ToolbarTooltipIntersectionMarker />

          {/* Content payload */}
          {payload ? (
            <>
              <span>{payload.label}</span>

              {payload.shortcut ? (
                <span className="ml-1 text-muted-foreground">
                  <KbdShortcuts hotkey={payload.shortcut} />
                </span>
              ) : null}
            </>
          ) : null}
        </TooltipContent>
      )}
    </Tooltip>
  )
}

/**
 * Detached trigger for {@link CellToolbarTooltipRoot}
 */
function CellToolbarTooltip({
  label,
  shortcut,
  children,
}: CellToolbarTooltipPayload & {
  children: React.ReactElement
}) {
  return (
    <TooltipTrigger
      handle={cellToolbarTooltipHandle}
      payload={{ label, shortcut }}
      aria-label={label}
      render={children}
    />
  )
}

/**
 * "Magic" number: Tooltip can float over column header,
 * but closes before covering "Over/Under" display.
 */
const TOOLTIP_SAFE_TOP = 90

/**
 * Helper component that sits at top edge of toolbaritem's tooltip.
 * It mounts an IntersectionObserver to detect when the tooltip
 * has crossed above a "safe" threshold, ie. when it's floating
 * too far above the sticky header and away from the (hidden) toolbar.
 */
function ToolbarTooltipIntersectionMarker() {
  const markerRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    const marker = markerRef.current
    if (!marker) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return
        // `isIntersecting` can also be false at other viewport edges,
        // so explicitly verify that it crossed the top boundary.
        if (
          !entry.isIntersecting &&
          entry.boundingClientRect.top < TOOLTIP_SAFE_TOP
        ) {
          cellToolbarTooltipHandle.close()
        }
      },
      {
        root: null,
        rootMargin: `-${TOOLTIP_SAFE_TOP}px 0px 0px 0px`,
        threshold: 0,
      },
    )

    observer.observe(marker)

    return () => {
      observer.disconnect()
    }
  }, [])

  return (
    <span
      ref={markerRef}
      aria-hidden
      className="pointer-events-none absolute left-1/2 top-0 size-px"
    />
  )
}

export { CellToolbarTooltip, CellToolbarTooltipRoot }
